import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Form, { FormField } from "src/components/ui/Form";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { genderOptions } from "src/constants/common";
import { toast } from "react-toastify";

interface UserLocationState {
  user?: any;
}

interface TenantUserFormProps {
  tenantUserId?: string;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const TenantUserForm: React.FC<TenantUserFormProps> = ({
  tenantUserId,
  onCancel,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requestedList, setRequestedList] = useState(false);

  const dispatch = useAppDispatch();
  const { createTenantUser, editTenantUser, tenantUserList } = useAppSelector(
    (state) => state.tenantUser
  );
  const { tenantRoleList } = useAppSelector((state) => state.tenant);

  const locationState = location.state as UserLocationState | undefined;
  const userFromState = locationState?.user || null;

  const userFromList = useMemo(() => {
    if (!tenantUserId) return null;
    return (
      tenantUserList?.data?.data?.find((item) => item?._id === tenantUserId) ||
      null
    );
  }, [tenantUserId, tenantUserList?.data?.data]);

  const activeUser = userFromState || userFromList;

  const normalizedInitialState = useMemo(() => {
    if (!activeUser) return {};
    const roleId =
      typeof activeUser.role === "string"
        ? activeUser.role
        : activeUser.role?._id || "";
    return {
      ...activeUser,
      role: roleId,
      password: "",
    };
  }, [activeUser]);

  useEffect(() => {
    dispatch(tenantActions.getTenantRoleListRequest({}));
  }, [dispatch]);

  useEffect(() => {
    if (tenantUserId && !activeUser && !requestedList) {
      dispatch(
        tenantUserActions.getTenantUserListRequest({
          page: 1,
          limit: 1000,
        })
      );
      setRequestedList(true);
    }
  }, [tenantUserId, activeUser, requestedList, dispatch]);

  useEffect(() => {
    if (tenantUserId && requestedList && !activeUser && tenantUserList?.data) {
      toast.error("User not found");
      if (onCancel) {
        onCancel();
      } else {
        navigate("/app/team-management/user-list");
      }
    }
  }, [
    tenantUserId,
    requestedList,
    activeUser,
    tenantUserList?.data,
    navigate,
    onCancel,
  ]);

  const handleClose = () => {
    dispatch(tenantUserActions.resetCreateTenantUser());
    dispatch(tenantUserActions.resetEditTenantUser());
    if (onCancel) {
      onCancel();
    } else {
      navigate("/app/team-management/user-list");
    }
  };

  const handleSuccess = () => {
    dispatch(tenantUserActions.resetCreateTenantUser());
    dispatch(tenantUserActions.resetEditTenantUser());
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate("/app/team-management/user-list");
  };

  useEffect(() => {
    if (createTenantUser.data) {
      toast.success("User created successfully");
      handleSuccess();
    } else if (createTenantUser.error) {
      toast.error(`Error: ${createTenantUser.error}`);
    }
  }, [createTenantUser, onSuccess]);

  useEffect(() => {
    if (editTenantUser.data) {
      toast.success("User modified successfully");
      handleSuccess();
    } else if (editTenantUser.error) {
      toast.error(`Error: ${editTenantUser.error}`);
    }
  }, [editTenantUser, onSuccess]);

  const fields: FormField[] = useMemo(
    () => [
      {
        label: "First Name",
        name: "firstName",
        type: "text",
        placeholder: "Enter first name",
        validations: { required: true },
      },
      {
        label: "Last Name",
        name: "lastName",
        type: "text",
        placeholder: "Enter last name",
        validations: { required: true },
      },
      {
        label: "Email",
        name: "email",
        type: "text",
        placeholder: "Enter email address",
        validations: {
          required: true,
          regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
          message: "Invalid email format",
        },
      },
      {
        label: "Password",
        name: "password",
        type: "password",
        placeholder: "Enter password",
        validations: tenantUserId
          ? { required: false, optional: true }
          : { required: true },
      },
      {
        label: "Date of Birth",
        name: "dob",
        type: "text",
        placeholder: "dd/mm/yyyy",
        validations: { required: true },
      },
      {
        label: "Gender",
        name: "gender",
        type: "dropdown",
        options: genderOptions,
        validations: { required: true },
      },
      {
        label: "Role",
        name: "role",
        type: "dropdown",
        options:
          tenantRoleList?.data?.data?.map((tenantRole: any) => ({
            label: tenantRole?.name || "",
            value: tenantRole?._id || "",
          })) || [],
        validations: { required: true },
      },
    ],
    [tenantRoleList?.data?.data, tenantUserId]
  );

  const handleSubmit = (data: Record<string, any>) => {
    if (tenantUserId) {
      const payload = { ...data };
      if (!payload.password) {
        delete payload.password;
      }
      dispatch(
        tenantUserActions.editTenantUserRequest({
          ...(payload as any),
          _id: tenantUserId,
        })
      );
      return;
    }

    dispatch(
      tenantUserActions.createTenantUserRequest({
        ...(data as any),
      })
    );
  };

  return (
    <Form
      initialState={normalizedInitialState as any}
      fields={fields}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      submitLabel={tenantUserId ? "Update User" : "Create User"}
      loading={createTenantUser.loading || editTenantUser.loading}
    />
  );
};

export default TenantUserForm;
